import { useState, useEffect, useRef } from "react";
import posthog from "posthog-js";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DailyRewardsStatus,
} from "../../models/apiTypes";
import type { UserData } from "../../models/user";
import {
  activateUserItemUserInventoryUserItemIdActivatePost,
  claimDailyRewardEndpointDailyRewardsClaimPost,
  getDailyRewardsStatusEndpointDailyRewardsStatusGet,
} from "../../client";
import { showAlert } from "../../utils/telegram/alert";
import { impactOccurred } from "../../utils/telegram/hapticFeedback";
import ModalFrame from "../primitives/ModalFrame";
import useSound from "use-sound";
import useSoundStore from "../../stores/useSoundStore";
import { applyUserProgressDelta } from "../../utils/userProgress";
import { REWARD_CONFIG } from "../dailyRewards/constants";
import MysteryBoxRewardModal from "../dailyRewards/MysteryBoxRewardModal";
import DailyRewardStreakBar from "../dailyRewards/DailyRewardStreakBar";
import DailyRewardDayCard from "../dailyRewards/DailyRewardDayCard";


interface DailyRewardsModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function DailyRewardsModal({ isOpen, onClose }: DailyRewardsModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { soundsEnabled } = useSoundStore();
  const trackedModalOpenRef = useRef(false);
  const trackedModalContentRef = useRef(false);
  const [playExpGift] = useSound('/exp-gift.mp3', {
    volume: 0.026,
    soundEnabled: soundsEnabled,
  });
  const [playCurrencyGift] = useSound('/currency-gift.mp3', {
    volume: 0.039,
    soundEnabled: soundsEnabled,
  });
  // Використовуємо Set для відстеження днів що обробляються (дозволяє одночасну обробку)
  const [claimingDays, setClaimingDays] = useState<Set<number>>(new Set());
  const [mysteryBoxReward, setMysteryBoxReward] = useState<{ type: "exp" | "pearls"; amount: number; isLoading?: boolean } | null>(null);
  // Відстежуємо, які дні вже мають забрану нагороду (оптимістично)
  const [claimedDays, setClaimedDays] = useState<Set<number>>(new Set());

  const { data: status, isLoading } = useQuery<DailyRewardsStatus>({
    queryKey: ["dailyRewardsStatus"],
    queryFn: async () => {
      const { data } = await getDailyRewardsStatusEndpointDailyRewardsStatusGet({ throwOnError: true });
      return data;
    },
    enabled: isOpen,
    retry: 3,
  });

  useEffect(() => {
    if (!isOpen) {
      trackedModalOpenRef.current = false;
      trackedModalContentRef.current = false;
      return;
    }
    if (trackedModalOpenRef.current) return;
    trackedModalOpenRef.current = true;
    try {
      posthog.capture("daily_rewards_modal_shown");
    } catch {
      /* never block UI */
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (!status || isLoading || trackedModalContentRef.current) return;
    trackedModalContentRef.current = true;
    const claimableDays = status.days.filter((d) => d.can_claim).map((d) => d.day);
    try {
      posthog.capture("daily_rewards_modal_content_ready", {
        current_streak: status.current_streak,
        max_available_day: status.max_available_day,
        claimable_days_count: claimableDays.length,
        claimable_days: claimableDays,
      });
    } catch {
      /* never block UI */
    }
  }, [isOpen, status, isLoading]);

  const handleClaim = async (day: number) => {
    // Перевіряємо чи цей конкретний день вже обробляється
    if (claimingDays.has(day)) return;
    
    // Оптимістичне оновлення UI - одразу позначаємо як забрану перед встановленням claimingDays
    setClaimedDays(prev => new Set(prev).add(day));
    setClaimingDays(prev => new Set(prev).add(day));
    // Найсильніша вібрація для daily rewards
    impactOccurred("heavy");

    // Визначаємо тип нагороди
    const config = REWARD_CONFIG[day];
    const rewardType = config?.type;
    const isMysteryBox = config?.type === "mystery";
    try {
      posthog.capture("daily_reward_claim_clicked", { day, reward_type: rewardType });
    } catch {
      /* never block UI */
    }

    // Для mystery box - одразу показуємо модалку з лоадером
    if (isMysteryBox) {
      setMysteryBoxReward({
        type: "exp", // Тимчасове значення, буде оновлено з бекенду
        amount: 0, // Тимчасове значення, буде оновлено з бекенду
        isLoading: true, // Показуємо лоадер
      });
    } else {
      // Для не mystery box - програємо звук одразу та оновлюємо userData оптимістично
      if (rewardType === "exp") {
        playExpGift();
        const expAmount = parseInt(config.label.replace(/,/g, "")) || 0;
        queryClient.setQueryData(["userData"], (old: UserData | undefined) => {
          if (!old) return old;
          return applyUserProgressDelta(old, { expDelta: expAmount });
        });
      } else if (rewardType === "pearls") {
        playCurrencyGift();
        const currencyAmount = parseInt(config.label.replace(/,/g, "")) || 0;
        queryClient.setQueryData(["userData"], (old: UserData | undefined) => {
          if (!old) return old;
          return applyUserProgressDelta(old, { pearlDelta: currencyAmount });
        });
      }
    }
    
    // НЕ оновлюємо userData через invalidateQueries ПЕРЕД запитом - воно вже оновлено оптимістично

    try {
      const { data: response } = await claimDailyRewardEndpointDailyRewardsClaimPost({
        body: { day },
        throwOnError: true,
      });
      
      if (response.user_item_id === null) {
        try {
          posthog.capture("daily_reward_claim_failed", {
            day,
            reward_type: rewardType,
            reason: "unavailable_or_already_claimed",
          });
        } catch {
          /* never block UI */
        }
        // Відкатуємо оптимістичне оновлення при помилці
        setClaimedDays(prev => {
          const newSet = new Set(prev);
          newSet.delete(day);
          return newSet;
        });
        // Закриваємо модалку mystery box, якщо вона відкрита
        if (isMysteryBox) {
          setMysteryBoxReward(null);
        }
        showAlert(t("This reward has already been claimed or is not available"));
        setClaimingDays(prev => {
          const newSet = new Set(prev);
          newSet.delete(day);
          return newSet;
        });
        return;
      }

      if (!isMysteryBox) {
        try {
          posthog.capture("daily_reward_claimed", { day, reward_type: rewardType });
        } catch {
          /* never block UI */
        }
      }

      try {
        // Активуємо предмет в фоні напряму через user_item_id (прибрали зайний запит getUserInventory)
        if (response.user_item_id) {
          const { data: activateResponse } = await activateUserItemUserInventoryUserItemIdActivatePost({
            path: { user_item_id: response.user_item_id },
            throwOnError: true,
          });
          
          // НЕ оновлюємо userData через invalidateQueries - воно вже оновлено оптимістично

          if (activateResponse.reward && isMysteryBox) {
            const reward = activateResponse.reward as {
              reward_type?: 'pearls' | 'exp';
              reward_amount?: number;
            };
            const finalRewardType = reward.reward_type === 'exp' ? 'exp' : 'pearls';
            const finalRewardAmount = reward.reward_amount ?? 0;
            try {
              posthog.capture("daily_reward_claimed", {
                day,
                reward_type: "mystery",
                resolved_reward_type: finalRewardType,
                resolved_amount: finalRewardAmount,
              });
            } catch {
              /* never block UI */
            }
            
            setMysteryBoxReward({
              type: finalRewardType,
              amount: finalRewardAmount,
              isLoading: false, // Прибираємо лоадер, показуємо нагороду
            });
            
            // Оптимістичне оновлення userData для mystery box
            if (finalRewardType === "exp") {
              queryClient.setQueryData(["userData"], (old: UserData | undefined) => {
                if (!old) return old;
                return applyUserProgressDelta(old, { expDelta: finalRewardAmount });
              });
              playExpGift();
            } else if (finalRewardType === "pearls") {
              queryClient.setQueryData(["userData"], (old: UserData | undefined) => {
                if (!old) return old;
                return applyUserProgressDelta(old, { pearlDelta: finalRewardAmount });
              });
              playCurrencyGift();
            }
          } else if (isMysteryBox) {
            try {
              posthog.capture("daily_reward_claimed", {
                day,
                reward_type: "mystery",
                resolved_reward_type: null,
              });
            } catch {
              /* never block UI */
            }
          }
          // Для не mystery box не показуємо алерт - візуально видно що нагорода отримана
        }
        // Не показуємо алерт навіть якщо немає предмета - візуально видно що нагорода отримана
      } catch (activateError: unknown) {
        console.error("Failed to activate reward:", activateError);
        if (isMysteryBox) {
          try {
            posthog.capture("daily_reward_activation_failed", {
              day,
              reward_type: "mystery",
            });
          } catch {
            /* never block UI */
          }
        }
        const errorMsg = activateError instanceof Error
          ? activateError.message
          : t("Failed to activate reward");
        showAlert(`${t("Reward claimed, but activation error")}: ${errorMsg}`);
      } finally {
        // Знімаємо блокування для цього дня
        setClaimingDays(prev => {
          const newSet = new Set(prev);
          newSet.delete(day);
          return newSet;
        });
      }
    } catch (error: unknown) {
      console.error("Failed to claim reward:", error);
      try {
        posthog.capture("daily_reward_claim_failed", {
          day,
          reward_type: rewardType,
          reason: "api_error",
        });
      } catch {
        /* never block UI */
      }
      const errorMessage = error instanceof Error
        ? error.message
        : t("Failed to claim reward");
      showAlert(errorMessage);
      // Знімаємо блокування при помилці
      setClaimingDays(prev => {
        const newSet = new Set(prev);
        newSet.delete(day);
        return newSet;
      });
    }
  };

  if (!isOpen) return null;

  return (
    <ModalFrame
      isOpen={isOpen}
      onClose={onClose}
      title={`🎁 ${t("Daily rewards")}`}
      variant="dialog"
      className="max-w-[372px] rounded-xl border-[3px] border-deep-blue"
      backdropClassName="modal-frame-bottom-gradient [--modal-frame-gradient-max-width:372px]"
      showBackButton={true}
    >
      <div className="box-border flex w-full flex-col items-center gap-3xl">
          <div className="flex w-full flex-col items-center gap-sm text-center">
            <div className="w-full [font-family:Lato,sans-serif] text-[clamp(14px,3.5vw,16px)] leading-[1.25] font-normal text-white">
              {t("Come to the game for 7 days in a row and collect daily rewards")}
            </div>
          </div>

          {status && (
            <DailyRewardStreakBar status={status} />
          )}
          {isLoading ? (
            <div className="flex w-full items-center justify-center py-[40px] text-white">
              {t("Loading...")}
            </div>
          ) : status ? (
            <div className="flex w-full flex-col gap-lg">
              {status.days.map((dayData) => {
                // Використовуємо claimedDays для оптимістичного оновлення або is_claimed з бекенду
                const isClaimed = claimedDays.has(dayData.day) || dayData.is_claimed;
                const isClaiming = claimingDays.has(dayData.day);

                return (
                  <DailyRewardDayCard
                    key={dayData.day}
                    dayData={dayData}
                    isClaimed={isClaimed}
                    isClaiming={isClaiming}
                    onClaim={handleClaim}
                  />
                );
              })}
            </div>
          ) : (
            <div className="flex w-full items-center justify-center py-[40px] text-white">
              {t("Failed to load daily rewards")}
            </div>
          )}
        </div>

      <MysteryBoxRewardModal
        isOpen={mysteryBoxReward !== null}
        onClose={() => setMysteryBoxReward(null)}
        rewardType={mysteryBoxReward?.type || "exp"}
        rewardAmount={mysteryBoxReward?.amount || 0}
        isLoading={mysteryBoxReward?.isLoading || false}
      />
    </ModalFrame>
  );
}
